import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Evidence dropzone for the competency records (TASK-283.8). Several files,
// each direct-uploaded to ActiveStorage the moment it is chosen; the form then
// posts the signed blob ids as evidence[] instead of the bytes. The server
// (TrainingRecord / TrainingReview validations) stays the authority on type and
// size; the checks here only spare someone a pointless upload.
export default class extends Controller {
  static targets = ["zone", "input", "list", "ids", "error"]
  static values = {
    directUploadUrl: String,
    maxSizeMb: { type: Number, default: 20 },
    types: { type: Array, default: ["application/pdf", "image/jpeg", "image/png"] }
  }

  connect() {
    this.pending = 0
    this.depth = 0
  }

  browse(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  chosen() {
    this.add(this.inputTarget.files)
    this.inputTarget.value = ""
  }

  dragOver(event) {
    event.preventDefault()
    this.zoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave(event) {
    event.preventDefault()
    this.zoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.zoneTarget.classList.remove("border-blue-500", "bg-blue-50")
    this.add(event.dataTransfer.files)
  }

  add(fileList) {
    this.errorTarget.textContent = ""
    const rejected = []
    Array.from(fileList).forEach((file) => {
      if (!this.typesValue.includes(file.type)) return rejected.push(`${file.name} is not a PDF, JPG or PNG`)
      if (file.size >= this.maxSizeMbValue * 1024 * 1024) return rejected.push(`${file.name} is over ${this.maxSizeMbValue} MB`)
      this.upload(file)
    })
    if (rejected.length) this.errorTarget.textContent = `${rejected.join(". ")}.`
  }

  upload(file) {
    const row = this.buildRow(file)
    this.listTarget.appendChild(row)
    this.resize()
    this.setPending(1)
    const status = row.querySelector("[data-role='status']")

    new DirectUpload(file, this.directUploadUrlValue).create((error, blob) => {
      this.setPending(-1)
      if (error) {
        status.textContent = "Upload failed"
        status.classList.add("text-red-600")
        return
      }
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "evidence[]"
      hidden.value = blob.signed_id
      this.idsTarget.appendChild(hidden)
      row.hidden_input = hidden
      status.textContent = "Ready"
    })
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("li")
    if (row.hidden_input) row.hidden_input.remove()
    row.remove()
    this.resize()
  }

  // On the record page this sits inside a collapsible whose open height was
  // fixed when it opened; growing the list would be clipped without this.
  resize() {
    const content = this.element.closest("[data-collapsible-target='content']")
    if (content && content.dataset.state === "open") content.style.maxHeight = `${content.scrollHeight}px`
  }

  setPending(delta) {
    this.pending += delta
    const form = this.element.closest("form")
    if (!form) return
    // Submit buttons live outside this element (the slideover's form-actions).
    form.querySelectorAll("[type='submit']").forEach((button) => { button.disabled = this.pending > 0 })
  }

  buildRow(file) {
    const row = document.createElement("li")
    row.className = "flex items-center justify-between gap-3 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
    row.dataset.role = "evidence-file"
    const text = document.createElement("span")
    text.className = "min-w-0"
    const name = document.createElement("span")
    name.className = "block truncate font-medium text-gray-900"
    name.textContent = file.name
    const meta = document.createElement("span")
    meta.className = "block text-xs text-gray-500"
    meta.append(`${this.size(file.size)} · `)
    const status = document.createElement("span")
    status.dataset.role = "status"
    status.textContent = "Uploading…"
    meta.appendChild(status)
    text.append(name, meta)
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "shrink-0 text-gray-500 hover:text-gray-700"
    remove.setAttribute("aria-label", `Remove ${file.name}`)
    remove.dataset.action = "click->evidence-dropzone#remove"
    remove.textContent = "×"
    row.append(text, remove)
    return row
  }

  size(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
}
